import {INewProjectMetadata, IR23Report, IR23ReportMetadata} from '@/interfaces/report';
import {IAsphaltDesignImperial} from '@/modules/design/interfaces/newAsphalt';
import {
  DesignScenarioType,
  EsalCalculationMethods,
  IDesignFlexibleSubstructureLayer,
  IDesignFlexibleSurfaceLayer,
} from '@/modules/design/interfaces/shared';
import {UnitSystem} from '@/interfaces/units';
import {IConcreteDesignImperial} from '@/modules/design/interfaces/newConcrete';
import {BaseScenarioType} from '@/lib/types/IBaseScenario';
import {IPorousDesignImperial} from '@/modules/design/interfaces/porous';
import {
  IAcOverlayDesignImperial,
  IAcOverlayLayerImperial,
  StructuralEvaluationMethods,
} from '@/modules/design/interfaces/acOverlay';
import {User} from '@/interfaces/user';
import {userStore} from '@/store/store';

export const getProjectMetadataFromR23Report = function (meta: IR23ReportMetadata,
                                                         report: IR23Report,
                                                         user: User): INewProjectMetadata {
  const sm: INewProjectMetadata = {
    id: -1, // Reserved for autokey from server
    name: meta.reportTitle || 'Unnamed Project',
    user: user.id || -1,
    created: meta.created,
    modified: new Date(Date.now()).toISOString(),
    visible: Boolean(meta.visible),
    isComputedFromLegacy: true,
    r23ReportId: meta.r23ReportId,
    displayUnits: UnitSystem.Imperial,
    owner: null,
    description: null,
    state: report.stateLabel || null,
  };

  return sm;
};

export const getUserId = () => {
  if (userStore !== null && userStore !== undefined) {
    return userStore.user ? userStore.user.id : null;
  }
  return null;
};

export const getAsphaltDesignFromR23Report = function (meta: IR23ReportMetadata,
                                                       report: IR23Report,
                                                       projectId: number): IAsphaltDesignImperial {
  const user = () => {

  };
  const ds: IAsphaltDesignImperial = {
    project: projectId,
    id: null,
    user: getUserId(),
    created: meta.created,
    modified: meta.updated,
    visible: meta.visible,
    displayUnits: UnitSystem.Imperial,
    unitSystem: UnitSystem.Imperial,
    scenarioType: BaseScenarioType.design,
    designScenarioType: DesignScenarioType.asphalt,
    isComputedFromLegacy: true,

    name: report.projectName || '',
    description: report.determineScenarioName || null,
    state: report.stateValue || null,

    completionYear: Number(report.designYear),
    roadwayClassification: report.roadwayClassification || null,

    // Pavement Design
    pavementType: report.pavementType || null,

    // Design Parameters
    designPeriod_years: Number(report.designPeriod), // Years
    reliabilityLevel: Number(report.reliabilityLevel),
    standardError: Number(report.standardError),
    initialServiceability: Number(report.initialServiceability),
    terminalServiceability: report.terminalServiceability || null,
    deltaServiceability: report.deltaServiceability || null,

    // Traffic and Loading
    esalCalculatedFrom: EsalCalculationMethods.design,
    // From AADT
    initialTraffic: Number(report.initialTraffic),
    trafficGrowthRate: Number(report.trafficGrowthRate),
    esalGrowthRate: 10,

    // From ESALs
    esals: Number(report.esals),
    loadEquivalencyFactor: Number(report.loadEquivalencyFactor),

    // From Design ESALs
    totalDesignEsals: Number(report.totalDesignEsals),

    // Pavement Structure
    drainageCoefficient: Number(report.drainageCoefficient),
    minimumThickness: report.minimumThickness || null,

    multipleLifts: <boolean>report.flexible!.multiple_lifts,
    surfaceLayers: (function (r: IR23Report): IDesignFlexibleSurfaceLayer[] {
      const reportSurfaceLayers = r.flexible!.surface_layers || [];
      return reportSurfaceLayers.map((rl: any): IDesignFlexibleSurfaceLayer => <IDesignFlexibleSurfaceLayer>{
        id: null,
        coefficient: rl.coefficient,
        drainage: rl.drainage,
        liftTitle: rl.liftTitle,
        thickness: Number(rl.thickness) || 0,
        modulus: Number(rl.modulus) || 0,
      });
    }(report)),

    substructureLayers: (function (r: IR23Report): IDesignFlexibleSubstructureLayer[] {
      const reportLayers = r.flexible!.layers || [];
      return reportLayers.map((rl: any): IDesignFlexibleSubstructureLayer => ({
        id: null,
        name: 'layer',
        coefficient: rl.coefficient,
        drainage: rl.drainage,
        thickness: rl.thickness,
        type: rl.type,
        modulus: rl.modulus || 20000,
        fixedThickness: rl.fixedThickness,
      }));
    }(report)),

    // Pavement Substructure
    existingResilientModulus: Number(report.existingResilientModulus),
    designSubgradeModulus: Number(report.designSubgradeModulus) || null,

    soilType: null,
    subgrade: report.subgrade || null, // PSI
    cbr: null,
    rvalue: null,
    layerCoefficient: Number(report.layerCoefficient),
    modulusCalculationMethod: null,

  };
  return ds;
};

export const getConcreteDesignFromR23Report = function (meta: IR23ReportMetadata, report: IR23Report, projectId: number): IConcreteDesignImperial {
  // if (report.roadwayClassification){
  //   const roadwayClassification = DesignRoadwayClassification[report.roadwayClassification]
  // } else {
  //   const roadwayClassification = null;
  // }

  const ds: IConcreteDesignImperial = {
    id: null,
    user: getUserId(),
    project: projectId,
    created: meta.created,
    modified: meta.updated,
    visible: meta.visible,
    isComputedFromLegacy: true,
    displayUnits: UnitSystem.Imperial,
    scenarioType: BaseScenarioType.design,
    designScenarioType: DesignScenarioType.concrete,
    unitSystem: UnitSystem.Imperial,

    name: report.projectName || '',
    description: report.determineScenarioName || null,
    // state: report.stateValue || null,
    state: report.stateValue || 'US-WA',

    completionYear: Number(report.designYear),
    roadwayClassification: report.roadwayClassification || null,

    // Pavement Design
    pavementType: report.pavementType || null,

    // Design Parameters
    designPeriod_years: Number(report.designPeriod), // Years
    reliabilityLevel: Number(report.reliabilityLevel),
    standardError: Number(report.standardError),
    initialServiceability: Number(report.initialServiceability),
    terminalServiceability: report.terminalServiceability || null,
    deltaServiceability: report.deltaServiceability || null,

    nearestCity: report.nearestCity || 'Detroit',
    meanWindSpeed_mph: Number(report.meanWindSpeed || 10.2),
    meanPrecip_in: Number(report.meanPrecip || 4),
    meanTemp_degF: Number(report.meanTemp || 48.6),

    // Traffic and Loading
    esalCalculatedFrom: EsalCalculationMethods.design,
    // From AADT
    initialTraffic: Number(report.initialTraffic),
    trafficGrowthRate: Number(report.trafficGrowthRate),
    esalGrowthRate: 10,

    // From ESALs
    esals: Number(report.esals),
    loadEquivalencyFactor: Number(report.loadEquivalencyFactor),

    // From Design ESALs
    totalDesignEsals: Number(report.totalDesignEsals),


    // Pavement Structure
    ruptureModulus_psi: Number(report.ruptureModulus || 800),

    elasticModulus_psi: Number(report.elasticModulus || 4000000),
    poissonsRatio: Number(report.poissonsRatio || 0.3),

    jointSpacing_in: Number(report.jointSpacing || 170),
    loadTransferCoefficient: Number(report.loadTransferCoefficient || 3),
    edgeSupport: Number(report.edgeSupport || 1.01),

    // Pavement Substructure

    baseLayerType: report.baseLayerType || 'Fine-grained soil',
    baseModulus_psi: Number(report.baseModulus || 15000),
    baseThickness_in: Number(report.baseThickness || 6.5),
    drainageFactor: Number(report.drainageFactor || 1.2),
    slabFrictionCoefficient: Number(report.slabCoefficient || 1.1),
    subgradeReactionModulus_psiPerIn: Number(report.subgradeReactionModulus || 250),

  };
  return ds;
};


export const getPorousDesignFromR23Report = function (meta: IR23ReportMetadata, report: IR23Report, projectId: number): IPorousDesignImperial {
  // if (report.roadwayClassification){
  //   const roadwayClassification = DesignRoadwayClassification[report.roadwayClassification]
  // } else {
  //   const roadwayClassification = null;
  // }

  const ds: IPorousDesignImperial = {
    id: null,
    user: getUserId(),
    project: projectId,
    created: meta.created,
    modified: meta.updated,
    visible: meta.visible,
    isComputedFromLegacy: true,
    displayUnits: UnitSystem.Imperial,
    scenarioType: BaseScenarioType.design,
    designScenarioType: DesignScenarioType.porous,
    unitSystem: UnitSystem.Imperial,

    name: report.projectName || '',
    description: report.determineScenarioName || null,
    state: report.stateValue || null,

    completionYear: Number(report.designYear),
    roadwayClassification: report.roadwayClassification || null,

    // Pavement Design
    pavementType: report.pavementType || null,


    // Layers
    layer_0_coefficient: 0.44,
    layer_1_coefficient: 0.1,
    layer_2_coefficient: 0,

    layer_0_thickness_in: 6,
    layer_1_thickness_in: 19,
    layer_2_thickness_in: 0,

    layer_1_modulus_psi: 15000,
    layer_2_modulus_psi: 25000,

    cbr: null,
    rValue: null,
    modulusCalculationMethod: null,


    // Design Parameters
    designPeriod_years: Number(report.designPeriod), // Years
    reliabilityLevel: Number(report.reliabilityLevel),
    standardError: Number(report.standardError),
    initialServiceability: Number(report.initialServiceability),
    terminalServiceability: report.terminalServiceability || null,
    deltaServiceability: report.deltaServiceability || null,


    // Traffic and Loading
    esalCalculatedFrom: EsalCalculationMethods.design,
    // From AADT
    initialTraffic: Number(report.initialTraffic),
    trafficGrowthRate: Number(report.trafficGrowthRate),
    esalGrowthRate: 10,

    // From ESALs
    esals: Number(report.esals),
    loadEquivalencyFactor: Number(report.loadEquivalencyFactor),

    loadDiameter_in: 10,

    // From Design ESALs
    totalDesignEsals: Number(report.totalDesignEsals),

    // Pavement Structure
  };
  return ds;
};


export const getAcOverlayDesignFromR23Report = function (meta: IR23ReportMetadata, report: IR23Report, projectId: number): IAcOverlayDesignImperial {
  const convertExistingLayers = function (existingLayers: {
    type: string,
    thickness: string | number,
    coefficient: string | number,
    drainage: string | number,
    id: string | number,
    sn: string | number,
    suggestion: string
  }[]): IAcOverlayLayerImperial[] {
    if (existingLayers) {
      return existingLayers.map((L) => ({
        id: null,
        type: L.type,
        thickness_in: Number(L.thickness),
        layerCoefficient: Number(L.coefficient),
        drainageCoefficient: Number(L.drainage),
        sn: Number(L.sn),
      }));
    }
    return [
      {
        id: null,
        type: 'Asphalt - Open Graded',
        thickness_in: 6,
        layerCoefficient: 0.5,
        drainageCoefficient: 0.9,
        sn: 0.9,
      },
      {
        id: null,
        type: 'Cement treated base',
        thickness_in: 10,
        layerCoefficient: 0.5,
        drainageCoefficient: 0.9,
        sn: 0.9,
      },
    ];
  };

  const ds: IAcOverlayDesignImperial = {
    id: null,
    user: getUserId(),
    project: projectId,
    created: meta.created,
    modified: meta.updated,
    visible: meta.visible,
    isComputedFromLegacy: true,
    displayUnits: UnitSystem.Imperial,
    scenarioType: BaseScenarioType.design,
    designScenarioType: DesignScenarioType.acOverlay,
    unitSystem: UnitSystem.Imperial,

    name: report.projectName || '',
    description: report.determineScenarioName || null,
    state: report.stateValue || null,

    // Pavement Design
    completionYear: Number(report.designYear),
    roadwayClassification: report.roadwayClassification || null,
    pavementType: report.pavementType || null,

    // Layers - arbitrary initial values for now
    // TODO: convert actual layers
    existingLayers: report.existingLayers ? convertExistingLayers(report.existingLayers) : [
      {
        type: 'Asphalt - Open Graded',
        thickness_in: 6,
        layerCoefficient: 0.5,
        drainageCoefficient: 0.9,
        sn: 0.9,
      },
      {
        type: 'Cement treated base',
        thickness_in: 10,
        layerCoefficient: 0.5,
        drainageCoefficient: 0.9,
        sn: 0.9,
      },
    ],

    subgradeSoilType: null, // Not in original system
    subgradeRValue: null, // Not in original system
    subgradeCbr: null, // Not in original system
    modulusCalculationMethod: null,

    subgradeModulus_psi: Number(report.existingResilientModulus) || null,

    existingLayerCoeff: Number(report.existingLayerCoeff) || null,
    overlayLayerMinimumThickness_in: Number(report.overlayLayerMinimumThickness) || null,


    // Structural Evaluation
    structuralEvaluationMethod: StructuralEvaluationMethods.testing,

    alligatorLow: report.fatigueLow || '0',
    alligatorMed: report.fatigueMedium || '0',
    alligatorHigh: report.fatigueHigh || '0',

    transverseLow: report.transverseLow || '0',
    transverseMed: report.transverseMedium || '0',
    transverseHigh: report.transverseHigh || '0',

    coresTaken: Boolean(report.coresTaken) || null,
    crackCoresTaken: Boolean(report.crackCoresTaken) || null,
    topDownCracking: report.topDownCracking || null,

    crackDepth_in: 4,
    distressDepth_in: 1,
    stripping: false,

    millRemoveDistressed: false,
    removeDepth_in: 3,

    // Nondestructive testing
    designSubgradeModulus_psi: Number(report.designSubgradeModulus) || null,
    estimatedStructuralCoefficient: Number(report.estimatedStructuralCoefficient) || 100,
    combinedLayerStiffness: Number(report.combinedLayerStiffness) || null,

    // Design Parameters
    designPeriod_years: Number(report.designPeriod), // Years
    reliabilityLevel: Number(report.reliabilityLevel),
    standardError: Number(report.standardError),
    initialServiceability: Number(report.initialServiceability),
    terminalServiceability: report.terminalServiceability || null,
    deltaServiceability: report.deltaServiceability || null,


    // Traffic and Loading
    esalCalculatedFrom: EsalCalculationMethods.design,
    // From AADT
    initialTraffic: Number(report.initialTraffic),
    trafficGrowthRate: Number(report.trafficGrowthRate),
    esalGrowthRate: 10,

    // From ESALs
    esals: Number(report.esals),
    loadEquivalencyFactor: Number(report.loadEquivalencyFactor),

    // From Design ESALs
    totalDesignEsals: Number(report.totalDesignEsals),

  };
  return ds;
};
